<script setup>
import { computed, h, ref, watch } from "vue";

import clsx from "clsx";
import { RouterLink, useRouter } from "vue-router";

import DefaultAvatarIcon from "@/components/Icons/DefaultAvatarIcon.vue";
import DefaultGroupIcon from "@/components/Icons/DefaultGroupIcon.vue";
import Reputation from "@/components/Icons/Reputation.vue";
import Verified from "@/components/Icons/Verified.vue";
import { getReputationTooltip } from "@/helpers/reputationTooltipHelper.js";
import { BaseURL } from "@/global/baseUrls.js";

/**
 * @prop {String} src - Direct image URL to use for the avatar; takes priority over `storageId`.
 * @prop {String} href - When set, the avatar renders as a RouterLink to this path instead of a plain div.
 * @prop {String} storageId - Storage id used to build the avatar URL (BaseURL + storageId) when `src` isn't given.
 * @prop {String} size - Size variant class applied to the avatar (e.g. "lg", "xl", "md").
 * @prop {String} classes - Extra CSS classes merged onto the avatar element.
 * @prop {Boolean} verified - Shows the verified/admin badge overlay when true.
 * @prop {Number} width - Width (px) of the verified/admin badge icon.
 * @prop {Number} height - Height (px) of the verified/admin badge icon.
 * @prop {Boolean} isAdmin - When true (with `verified`), shows the Reputation badge instead of the Verified badge.
 * @prop {Boolean} isOrg - Marks this avatar as belonging to an organization/group (affects fallback icon and ring/border styling).
 * @prop {Number} rating - User's reputation/civic rating; drives the tooltip text and ring color, when provided.
 * @prop {Boolean} isCurrentUser - Whether this avatar represents the logged-in user, used to tailor the tooltip wording.
 * @prop {Number} civicScore - Additional civic score value (currently unused directly in logic below).
 */
const props = defineProps({
  src: {
    type: String,
    default: "",
  },
  href: {
    type: String,
    default: "",
  },
  storageId: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "",
  },
  classes: {
    type: String,
    default: "",
  },
  verified: {
    type: Boolean,
    default: false,
  },
  width: {
    type: Number,
    default: 16,
  },
  height: {
    type: Number,
    default: 16,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  isOrg: {
    type: Boolean,
    default: false,
  },
  // Add rating and isCurrentUser props for tooltip
  rating: {
    type: Number,
    default: null,
  },
  isCurrentUser: {
    type: Boolean,
    default: false,
  },
  civicScore: {
    type: Number,
    default: 0
  },
});


/** Resolves the avatar image URL: prefers the explicit `src` prop, then builds one from `storageId`, else empty. */
const getSrc = computed(() => {
  if (props.src) {
    return props.src;
  }
  if (props.storageId) {
    return BaseURL + props.storageId;
  }
  return "";
});

// Tracks a load failure on the real image above so the template can fall
// back to the inline default icon — resetting whenever the src itself
// changes so a new avatar gets its own chance to load.
const imgFailed = ref(false);
/** Resets the "image failed" flag whenever the resolved src changes, giving a new avatar its own load attempt. */
watch(getSrc, () => { imgFailed.value = false; });
/** True when there's no resolvable image src, or the real `<img>` failed to load — triggers the inline fallback icon. */
const showFallbackIcon = computed(() => !getSrc.value || imgFailed.value);
/** Chooses which fallback icon component to render: group icon for orgs, person icon otherwise. */
const FallbackIcon = computed(() => (props.isOrg ? DefaultGroupIcon : DefaultAvatarIcon));

// Compute tooltip text if rating is provided
/** Builds the hover tooltip text from the user's rating, or empty string when no rating is supplied. */
const tooltipText = computed(() => {
  if (props.rating !== null && props.rating !== undefined) {
    return getReputationTooltip(props.rating, props.isCurrentUser);
  }
  return "";
});
/** Whether `props.rating` is a usable numeric value (not null/undefined/NaN). */
const hasRating = computed(() => props.rating !== null && props.rating !== undefined && !Number.isNaN(Number(props.rating)));

//select civic color
/**
 * Maps the numeric rating into a ring/border color: green for high scores
 * (40-100), yellow for mid/neutral (-39 to 39), red for low (-100 to -40),
 * defaulting to yellow for anything unexpected.
 */
const ringColor = computed(() => {
  const score = Number(props.rating) || 0;
  if (score >= 40 && score <= 100) {
    return "#22c55e";  // green
  }

  if (score >= -39 && score <= 39) {
    // return "#facc15";  // yellow
    // return "rgb(34 194 94)";
    // return "var(--color-quick-link-icon-bg)";
    return "#facc15";
  }

  if (score >= -100 && score <= -40) {
    return "#ef4444";  // red
  }

  return "#facc15"; // default fallback
});

/** Inline style for the avatar's colored ring: no ring for orgs or when there's no rating, otherwise a solid border in `ringColor`. */
const ringStyle = computed(() => {
  if (props.isOrg || !hasRating.value) return {};
  // return { '--avatar-ring-background': `conic-gradient(${ringColor.value} 0%, ${ringColor.value} 100%)` };
  return { border: `2px solid ${ringColor.value}` };
});

// Ensure broken external images don't cascade errors or cause repeated reflows —
// swaps to the inline default icon (see showFallbackIcon) instead of retrying
// with another <img src>.
const newOnImgError = (e) => {
  if (import.meta.env.DEV && e?.target) {
    const originalSrc = e.target.src;
    const isInstagramCdn = originalSrc.includes('cdninstagram.com') || originalSrc.includes('scontent-');
    const isKnownExternalDomain = originalSrc.includes('sfstandard.com') || originalSrc.includes('content.sfstandard.com');
    if (!isInstagramCdn && !isKnownExternalDomain) {
      console.debug(`[BaseAvatar] Unexpected image load failure: ${originalSrc}`);
    }
  }
  imgFailed.value = true;
};
</script>

<template>
  <!-- Outer ring wrapper: :style applies the rating-based colored border (via `ringStyle`);
       :title shows the reputation tooltip text on hover, when one is available -->
  <div class="new-avatar-ring" :style="ringStyle" :title="tooltipText || undefined">
    <!-- Dynamic root element: renders as a RouterLink when `href` is supplied, otherwise a plain div;
         @click.stop prevents clicks from bubbling to any parent click handler -->
    <component @click.stop :is="props.href ? 'RouterLink' : 'div'" :to="props.href || undefined" class="new-avatar"
      :class="clsx({ 'border-none': isOrg }, size, classes)">
      <!-- Real avatar image, shown while a src is resolvable and hasn't failed to load; @error flips `showFallbackIcon` on -->
      <img v-if="!showFallbackIcon" class="new-avatar-img" :src="getSrc" alt="user's avatar" loading="lazy" decoding="async" @error="newOnImgError" />
      <!-- Fallback icon (person or group), rendered instead of the <img> when there's no usable src or it failed to load -->
      <component :is="FallbackIcon" v-else class="new-avatar-img" />
      <!-- Verified/admin badge overlay, only shown when the `verified` prop is true -->
      <span v-if="verified" class="absolute bottom-[-3px] right-[-3px] flex items-center">
        <!-- Chooses the Reputation badge for admins, Verified badge otherwise; sized via the width/height props -->
        <component :is="isAdmin ? Reputation : Verified" :style="{ width: `${width}px`, height: `${height}px` }" />
      </span>
    </component>
  </div>
</template>

<style scoped lang="scss">
.new-avatar {
  display: block;
  width: 36px;
  height: 36px;
  // border: 1px solid #71767b;
  position: relative;
  z-index: 1;
  border-radius: 100%;
  cursor: pointer;

  &-img {
    width: 100%;
    height: 100%;
    // border-radius: 50%;
    border-radius: 6px;
    object-fit: cover;
  }

  &.lg {
    width: 66px;
    height: 66px;
  }

  &.xl {
    width: 96px;
    height: 96px;
  }

  &.md {
    width: 60px;
    height: 60px;
  }
}

.new-avatar-ring {
  position: relative;
  isolation: isolate;
  padding: 0;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: fit-content;
  height: fit-content;
}

.new-avatar-ring::before {
  content: "";
  position: absolute;
  inset: -2px;
  border-radius: inherit;
  // background: var(--avatar-ring-background, transparent);
  z-index: 0;
}
.header-avatar .new-avatar-img {
    width: 100% !important;
    height: 100% !important;
    -o-object-fit: cover !important;
    object-fit: cover !important;
    border-radius: 8px !important;
}
</style>
